import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchInvitations, 
  setInvitationFilters,
  setSelectedInvitation,
  deleteInvitation,
  resendInvitation
} from '../../../store/slices/invitationSlice';
import { openModal } from '../../../store/slices/uiSlice';
import { 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import { LoadingSpinner, ErrorMessage } from '../../common';

// Helper to format dates
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Helper for status color
const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'primary';
    case 'used':
      return 'success';
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

const InvitationList = () => {
  const dispatch = useDispatch();
  const { items, loading, error, filters, stats } = useSelector(state => state.invitations);
  const [searchTerm, setSearchTerm] = useState('');
  
  // We'll let the parent component (InvitationsPage) handle fetching
  // This prevents duplicate fetches and infinite loops
  useEffect(() => {
    // Only log the current filters but don't fetch
    console.log('InvitationList received filters:', filters);
    console.log('InvitationList has items:', items.length);
    if (items.length > 0) {
      console.log('First invitation sample:', items[0]);
    }
  }, [filters, items.length]);
  
  // Handle filter changes
  const handleStatusChange = (e) => {
    dispatch(setInvitationFilters({ ...filters, status: e.target.value, page: 1 }));
  };
  
  // Handle search
  const handleSearch = () => {
    dispatch(setInvitationFilters({ ...filters, search: searchTerm, page: 1 }));
  };
  
  // Handle pagination
  const handlePageChange = (e, newPage) => {
    dispatch(setInvitationFilters({ ...filters, page: newPage + 1 }));
  };
  
  const handleRowsPerPageChange = (e) => {
    dispatch(setInvitationFilters({ 
      ...filters, 
      limit: parseInt(e.target.value, 10),
      page: 1 
    }));
  };
  
  // Actions
  const handleCreateClick = () => {
    dispatch(openModal('createInvitation'));
  };
  
  const handleDeleteClick = (id) => {
    if (window.confirm('Are you sure you want to delete this invitation?')) {
      dispatch(deleteInvitation(id));
    }
  };
  
  const handleResendClick = (id) => {
    dispatch(resendInvitation(id));
  };
  
  const handleViewClick = (invitation) => {
    dispatch(setSelectedInvitation(invitation));
    dispatch(openModal('invitationDetail'));
  };
  
  // Forward refresh action to the parent component through filters
  const handleRefresh = () => {
    // Instead of directly fetching, trigger a minor filter update that parent will detect
    const refreshedFilters = { ...filters, _refresh: Date.now() };
    dispatch(setInvitationFilters(refreshedFilters));
  };
  
  if (loading && !items.length) {
    return <LoadingSpinner message="Loading invitations..." />;
  }
  
  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h1">
          Astrologer Invitations
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          Create Invitation
        </Button>
      </Box>
      
      {error && <ErrorMessage message={error} />}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filters.status}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="all">All Invitations</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="used">Used</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
          />
          
          <Button 
            variant="outlined" 
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
        
        <IconButton onClick={handleRefresh} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>
      
      <Box display="flex" gap={2} mb={2}>
        <Chip label={`Total: ${stats.total}`} />
        <Chip label={`Pending: ${stats.pending}`} color="primary" />
        <Chip label={`Used: ${stats.used}`} color="success" />
        <Chip label={`Expired: ${stats.expired}`} color="error" />
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Specialization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No invitations found
                </TableCell>
              </TableRow>
            ) : (
              items.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>{invitation.id.substring(0, 8)}...</TableCell>
                  <TableCell>
                    {invitation.first_name || invitation.last_name
                      ? `${invitation.first_name || ''} ${invitation.last_name || ''}`.trim()
                      : '-'}
                  </TableCell>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>{invitation.specialization || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={invitation.status}
                      color={getStatusColor(invitation.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(invitation.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton onClick={() => handleViewClick(invitation)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {invitation.status === 'pending' && (
                      <Tooltip title="Resend Invitation">
                        <IconButton onClick={() => handleResendClick(invitation.id)}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteClick(invitation.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={stats.total}
        page={filters.page - 1}
        onPageChange={handlePageChange}
        rowsPerPage={filters.limit}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
};

export default InvitationList;
